<script setup>
import { Head, router } from "@inertiajs/vue3";
import ActionButton from "@js/components/ActionButton.vue";
import InputField from "@js/components/fields/InputField.vue";
import { useRoute } from "@js/utils/route";
import { Form } from "vee-validate";
import { reactive, ref } from "vue";
import { toast } from "vue3-toastify";
import { useI18n } from "vue-i18n";
import { object, string } from "yup";

const { t } = useI18n();

const { route } = useRoute();

const isSubmitting = ref(false);

const form = reactive({
  email: "",
});

const validationSchema = object({
  email: string().required(t("error.required")).email(t("error.email")),
});

function submit() {
  isSubmitting.value = true;

  fetch(route("front.postForgotPassword"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-TOKEN": document
        .querySelector('meta[name="csrf-token"]')
        .getAttribute("content"),
    },
    body: JSON.stringify({
      email: form.email,
    }),
  })
    .then((response) => response.json())
    .then((data) => {
      if (data.status === "passwords.sent") {
        toast.success(t("toast.passwordResetLinkSent", { email: form.email }), {
          position: "top-right",
        });
        router.visit(route("front.login"));
      } else if (data.status === "passwords.throttled") {
        toast.error(t("toast.passwordResetLinkThrottled"), {
          position: "top-right",
        });
      } else if (data.status === "passwords.user") {
        toast.error(t("toast.passwordResetLinkUserNotFound"), {
          position: "top-right",
        });
      } else {
        toast.error(t("toast.anErrorOccurred"), { position: "top-right" });
      }
    })
    .finally(() => {
      isSubmitting.value = false;
    })
    .catch((error) => {
      toast.error(error.message, { position: "top-right" });
    });
}
</script>

<template>
  <Head>
    <title>{{ t("title.forgotPassword") }} - {{ t("title.datakepler") }}</title>
  </Head>

  <div class="flex h-full flex-col items-center justify-center bg-gray-50 px-4">
    <img
      src="/images/logo/logo.svg"
      :alt="t('title.datakepler')"
      class="mb-6 w-48 md:w-60"
    />

    <Form
      v-slot="{ errors }"
      :validation-schema="validationSchema"
      class="w-full max-w-md rounded bg-white p-6 shadow"
      @submit="submit"
    >
      <div class="mb-6 text-2xl font-medium">
        {{ t("title.forgotPassword") }}
      </div>

      <InputField
        v-model="form.email"
        :label="t('all.label.email')"
        :placeholder="t('all.label.email')"
        field-name="email"
        field-id="email"
        type="email"
        required
      />

      <div class="mt-6">
        <ActionButton
          submit
          :disabled="Object.keys(errors).length > 0 || isSubmitting"
          class="w-full"
          size="large"
          :loading="isSubmitting"
        >
          {{ t("button.resetPassword") }}
        </ActionButton>
      </div>
    </Form>
  </div>
</template>
